/**
 * Core Web Vitals Page - Dedicated page for Core Web Vitals metrics
 * 
 * Shows desktop vs mobile performance metrics including:
 * - LCP (Largest Contentful Paint)
 * - INP (Interaction to Next Paint)
 * - CLS (Cumulative Layout Shift)
 * - TTFB (Time to First Byte)
 * 
 * Features:
 * - Mobile/Desktop tabs for device-specific views
 * - Snapshot date display (from measured_at)
 * - Per-URL performance table
 */

import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Fade,
  Button,
  Skeleton,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  PhoneAndroid as MobileIcon,
  Computer as DesktopIcon,
  Speed as SpeedIcon,
  CalendarToday as CalendarIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCoreWebVitals, CoreWebVitalsResponse, UrlCwvData } from '@/services/customer/coreWebVitalsApi';
import { SEMANTIC_COLORS } from '@/services/customer/constants';

type DeviceTab = 'mobile' | 'desktop';

const getRatingColor = (rating?: string) => {
  switch (rating) {
    case 'GOOD':
    case 'FAST':
      return SEMANTIC_COLORS.success;
    case 'NEEDS_IMPROVEMENT':
    case 'AVERAGE':
      return SEMANTIC_COLORS.warning;
    case 'POOR':
    case 'SLOW':
      return SEMANTIC_COLORS.error;
    default:
      return SEMANTIC_COLORS.neutral;
  }
};

const getScoreColor = (score?: number) => {
  if (score === undefined || score === null) return SEMANTIC_COLORS.neutral;
  if (score >= 90) return SEMANTIC_COLORS.success;
  if (score >= 50) return SEMANTIC_COLORS.warning;
  return SEMANTIC_COLORS.error;
};

const formatMetricValue = (value?: number, suffix: string = ''): string => {
  if (value === undefined || value === null) return 'N/A';
  if (suffix === 'ms') return `${Math.round(value)}ms`;
  if (suffix === 's') return `${(value / 1000).toFixed(2)}s`;
  return value.toFixed(3);
};

interface MetricCardProps {
  label: string;
  score?: number;
  rating?: string;
  unit?: string;
}

function MetricCard({ label, score, rating, unit = '' }: MetricCardProps) {
  const color = getRatingColor(rating);
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <Chip
            label={formatMetricValue(score, unit)}
            size="small"
            sx={{ bgcolor: color, color: 'white', fontWeight: 600, minWidth: 70 }}
          />
          <Typography variant="caption" color="text.secondary">
            {rating && rating !== 'UNKNOWN' ? rating.replace('_', ' ') : ''}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

// URL List Panel Component
function UrlCwvPanel({ 
  urls, 
  deviceTab 
}: { 
  urls: UrlCwvData[]; 
  deviceTab: DeviceTab;
}) {
  const [expanded, setExpanded] = useState(true);

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">Page Performance</Typography>
            <Chip label={`${urls.length} URLs`} size="small" color="primary" variant="outlined" />
          </Box>
          <IconButton size="small">
            {expanded ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </Box>

        <Collapse in={expanded} timeout="auto">
          <Box sx={{ mt: 2 }}>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell>URL</TableCell>
                    <TableCell align="center" width={80}>Score</TableCell>
                    <TableCell align="center" width={100}>Category</TableCell>
                    <TableCell align="center" width={90}>LCP</TableCell>
                    <TableCell align="center" width={70}>CLS</TableCell>
                    <TableCell align="center" width={90}>TTFB</TableCell>
                    <TableCell width={100}>Measured</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {urls.map((entry) => {
                    const score = deviceTab === 'mobile' ? entry.performanceScoreMobile : entry.performanceScoreDesktop;
                    const category = deviceTab === 'mobile' ? entry.performanceCategoryMobile : entry.performanceCategoryDesktop;
                    const lcp = deviceTab === 'mobile' ? entry.lcpMobile : entry.lcpDesktop;
                    const cls = deviceTab === 'mobile' ? entry.clsMobile : entry.clsDesktop;
                    const ttfb = deviceTab === 'mobile' ? entry.ttfbMobile : entry.ttfbDesktop;
                    
                    return (
                      <TableRow key={entry.url} hover>
                        <TableCell>
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              maxWidth: 350,
                              '&:hover': { textDecoration: 'underline', cursor: 'pointer' },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(entry.url, '_blank');
                            }}
                          >
                            {entry.url.replace(/^https?:\/\/[^/]+/, '')}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {score !== undefined && score !== null ? (
                            <Chip
                              label={score}
                              size="small"
                              sx={{
                                bgcolor: getScoreColor(score),
                                color: 'white',
                                fontWeight: 600,
                                minWidth: 45,
                              }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {category ? (
                            <Chip
                              label={category}
                              size="small"
                              sx={{
                                bgcolor: getRatingColor(category),
                                color: 'white',
                                fontSize: 10,
                                height: 22,
                              }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="caption">
                            {formatMetricValue(lcp, 'ms')}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="caption">
                            {cls !== undefined && cls !== null ? cls.toFixed(3) : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="caption">
                            {formatMetricValue(ttfb, 'ms')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {entry.measuredAt
                              ? new Date(entry.measuredAt).toLocaleDateString()
                              : '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}

// Device Summary Card
function DeviceSummaryCard({ 
  device, 
  data,
  snapshotDate,
}: { 
  device: DeviceTab; 
  data: CoreWebVitalsResponse;
  snapshotDate?: string;
}) {
  const metrics = device === 'mobile' ? data.mobile : data.desktop;
  const Icon = device === 'mobile' ? MobileIcon : DesktopIcon;
  const score = metrics.overallScore;
  const color = getScoreColor(score);

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Icon color="action" />
          <Typography variant="h6">{device === 'mobile' ? 'Mobile' : 'Desktop'}</Typography>
        </Box>

        {score !== undefined && score !== null ? (
          <>
            <Typography variant="h2" sx={{ color, mb: 1 }}>
              {Math.round(score)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Overall Performance Score
            </Typography>
            <LinearProgress
              variant="determinate"
              value={score}
              sx={{
                mt: 2,
                height: 8,
                borderRadius: 4,
                bgcolor: 'rgba(0,0,0,0.1)',
                '& .MuiLinearProgress-bar': { bgcolor: color },
              }}
            />
            {snapshotDate && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Snapshot: {new Date(snapshotDate).toLocaleDateString()}
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No data available
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function CoreWebVitalsPage() {
  const navigate = useNavigate();
  const [deviceTab, setDeviceTab] = useState<DeviceTab>('mobile');
  const { data, isLoading } = useCoreWebVitals();

  const currentMetrics = useMemo(() => {
    if (!data) return null;
    return deviceTab === 'mobile' ? data.mobile : data.desktop;
  }, [data, deviceTab]);

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  return (
    <Fade in timeout={300}>
      <Box sx={{ maxWidth: { xs: 400, sm: 768, md: 900, lg: 1200 }, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: 3 }}>
        {/* Header with Snapshot Display */}
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/early-stage')}
            sx={{ mb: 2 }}
          >
            Back to Dashboard
          </Button>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SpeedIcon />
                Core Web Vitals
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Monitor your site's user experience metrics across desktop and mobile devices.
              </Typography>
            </Box>

            {/* Snapshot Info */}
            {data?.snapshotDate && (
              <Box sx={{ minWidth: 200, textAlign: 'right' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    justifyContent: 'flex-end',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    px: 2,
                    py: 1,
                  }}
                >
                  <CalendarIcon fontSize="small" color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Data Snapshot
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {new Date(data.snapshotDate).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                      {data.snapshotDate === new Date().toISOString().split('T')[0] && (
                        <Chip label="Latest" size="small" color="primary" sx={{ ml: 1, height: 18 }} />
                      )}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {data.summary.totalUrls} URLs analyzed
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Device Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={deviceTab}
            onChange={(_, newValue) => setDeviceTab(newValue)}
            variant="fullWidth"
          >
            <Tab
              value="mobile"
              icon={<MobileIcon />}
              iconPosition="start"
              label="Mobile"
            />
            <Tab
              value="desktop"
              icon={<DesktopIcon />}
              iconPosition="start"
              label="Desktop"
            />
          </Tabs>
        </Paper>

        {!data || data.summary.totalUrls === 0 ? (
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                No Core Web Vitals data available yet. Run PageSpeed Insights analysis to collect performance metrics.
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <DeviceSummaryCard device={deviceTab} data={data} snapshotDate={data.snapshotDate} />
              </Grid>
              <Grid item xs={12} md={8}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Core Metrics - {deviceTab === 'mobile' ? 'Mobile' : 'Desktop'}
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={6} md={3}>
                        <MetricCard
                          label="LCP (Largest Contentful Paint)"
                          score={currentMetrics?.lcp.score}
                          rating={currentMetrics?.lcp.rating}
                          unit="ms"
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <MetricCard
                          label="INP (Interaction to Next Paint)"
                          score={currentMetrics?.fid.score}
                          rating={currentMetrics?.fid.rating}
                          unit="ms"
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <MetricCard
                          label="CLS (Cumulative Layout Shift)"
                          score={currentMetrics?.cls.score}
                          rating={currentMetrics?.cls.rating}
                        />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <MetricCard
                          label="TTFB (Time to First Byte)"
                          score={currentMetrics?.ttfb.score}
                          rating={currentMetrics?.ttfb.rating}
                          unit="ms"
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* URL Performance Table */}
            {data.urls && data.urls.length > 0 && (
              <UrlCwvPanel
                urls={data.urls}
                deviceTab={deviceTab}
              />
            )}
          </>
        )}
      </Box>
    </Fade>
  );
}

export default CoreWebVitalsPage;
